<template>
  <div v-if="infoWindow" class="flex flex-col gap-16 max-w-480">
    <!-- Name and Address -->
    <div class="flex flex-col items-start justify-start gap-2">
      <div class="type-headline-base">{{ store.name }}</div>
      <div class="type-sm text-text-subtle">{{ store.addressLine1 }}, <span v-if="globalContent.showRetailerPostalCode">{{ store.postalcode }}</span> {{ store.city }}</div>
      <div v-if="store.distance !== undefined" class="type-sm text-text-subtle">
        {{ formatDistance(store.distance) }}
      </div>
    </div>

    <!-- Exhibition Details -->
    <div v-if="store.exibitionRating >= 2 && store.exibitionRating <= 4 && !globalContent.disableRetailerExhibitionDetails" class="flex flex-col gap-8">
      <div class="flex items-center gap-4">
        <span
          v-for="n in store.exibitionRating"
          :key="n"
          class="w-8 h-8 rounded-full"
          :class="n <= store.exibitionRating
            ? 'bg-bg-brandStrong'
            : 'bg-bg-base border border-border-base'"
        />
      </div>
      <div>
        <div class="type-headline-sm">{{ $t(`findRetailer.rating${store.exibitionRating}.name`) }}</div>
        <div class="type-sm text-text-subtle">{{ $t(`findRetailer.rating${store.exibitionRating}.description`) }}</div>
      </div>
    </div>

    <!-- Floor Types -->
    <div v-if="floors.length" class="flex flex-wrap gap-8">
      <div
        v-for="(floor, idx) in floors"
        :key="idx"
        class="inline-block px-8 py-4 type-headline-xs text-text-attention bg-bg-attention"
      >
        {{ floor }}
      </div>
    </div>

    <!-- Contact Information -->
    <div class="w-full flex flex-col gap-16">
      <div class="flex flex-col justify-start items-start gap-8 type-sm text-text-subtle">
        <a
          v-if="store.phone"
          :href="`tel:${store.phone}`"
          class="inline-flex items-center gap-8 hover:underline"
        >
          <img
            src="/icons/phone.svg"
            class="w-20 h-20"
            alt="Call phone"
          >
          {{ store.phone }}
        </a>
        <a
          v-if="store.email"
          :href="`mailto:${store.email}`"
          class="inline-flex items-center gap-8 hover:underline"
        >
          <img
            src="/icons/mail.svg"
            class="w-20 h-20"
            alt="Send email"
          >
          {{ store.email }}
        </a>
        <a
          v-if="store.website"
          :href="store.website"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-8 hover:underline"
        >
          <img
            src="/icons/globe-alt.svg"
            class="w-20 h-20"
            alt="Visit website"
          >
          {{ store.website }}
        </a>
      </div>

      <!-- Book a Meeting -->
      <nuxt-link
        v-if="store.bookAppointment"
        class="btn btn--lg w-full"
        :to="$manualUrl(`retailer?id=${store.accountid}`)"
      >
        {{ $t('findRetailer.bookAppointment') }}
      </nuxt-link>
    </div>
  </div>
  <div
    v-else
    class="
    relative w-full flex flex-col items-start justify-start gap-16 p-16 desk:p-24
    bg-bg-base outline outline-1 outline-bg-inverse/10 shadow-sm
    transition-all duration-300 ease-out"
    :class="{
      'outline-bg-inverse/25 shadow cursor-auto': expanded,
      'cursor-pointer hover:outline-bg-inverse/25 hover:shadow': !expanded
    }"
    @click="handleSelect"
  >
    <!-- Close Button -->
    <button class="absolute top-16 right-16" @click.stop="toggleClose">
      <img
        src="/icons/chevron-down.svg"
        class="w-20 h-20 transition-all"
        alt="Open / Close details"
        :class="{
          'rotate-180': expanded
        }"
      >
    </button>

    <!-- Name and Address -->
    <div class="flex flex-col items-start justify-start gap-2">
      <div class="type-headline-base">{{ store.name }}</div>
      <div class="type-sm text-text-subtle">{{ store.addressLine1 }}, <span v-if="globalContent.showRetailerPostalCode">{{ store.postalcode }}</span> {{ store.city }}</div>
      <div v-if="store.distance !== undefined" class="type-sm text-text-subtle">
        {{ formatDistance(store.distance) }}
      </div>
    </div>

    <!-- Exhibition Details -->
    <div v-if="store.exibitionRating >= 2 && store.exibitionRating <= 4 && !globalContent.disableRetailerExhibitionDetails" class="flex flex-col gap-8">
      <div class="flex items-center gap-4">
        <span
          v-for="n in store.exibitionRating"
          :key="n"
          class="w-8 h-8 rounded-full"
          :class="n <= store.exibitionRating
            ? 'bg-bg-brandStrong'
            : 'bg-bg-base border border-border-base'"
        />
      </div>
      <div class="inline-flex items-center gap-4 type-headline-sm">
        {{ $t(`findRetailer.rating${store.exibitionRating}.name`) }}
        <Tooltip :text="$t(`findRetailer.rating${store.exibitionRating}.description`)" :show-on-hover="true">
          <img
            src="/icons/question-circle.svg"
            class="w-16 h-16"
            alt="Info"
          >
        </Tooltip>
      </div>
    </div>

    <!-- Floor Types -->
    <div v-if="floors.length" class="flex flex-wrap gap-8">
      <div
        v-for="(floor, idx) in floors"
        :key="idx"
        class="inline-block px-8 py-4 type-headline-xs text-text-attention bg-bg-attention"
      >
        {{ floor }}
      </div>
    </div>

    <!-- Expanded Content -->
    <div v-show="expanded" class="w-full flex flex-col gap-16 overflow-y-hidden">
      <!-- Contact Information -->
      <div class="flex flex-col justify-start items-start gap-8 type-sm text-text-subtle">
        <a
          v-if="store.phone"
          :href="`tel:${store.phone}`"
          class="inline-flex items-center gap-8 hover:underline"
        >
          <img
            src="/icons/phone.svg"
            class="w-20 h-20"
            alt="Call phone"
          >
          {{ store.phone }}
        </a>
        <a
          v-if="store.email"
          :href="`mailto:${store.email}`"
          class="inline-flex items-center gap-8 hover:underline"
        >
          <img
            src="/icons/mail.svg"
            class="w-20 h-20"
            alt="Send email"
          >
          {{ store.email }}
        </a>
        <a
          v-if="store.website"
          :href="store.website"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-8 hover:underline"
        >
          <img
            src="/icons/globe-alt.svg"
            class="w-20 h-20"
            alt="Visit website"
          >
          {{ store.website }}
        </a>
      </div>

      <!-- Book a Meeting -->
      <nuxt-link
        v-if="store.bookAppointment && !globalContent.disableRetailerExhibitionDetails"
        class="btn btn--lg w-full"
        :to="$manualUrl(`retailer?id=${store.accountid}`)"
      >
        {{ $t('findRetailer.bookAppointment') }}
      </nuxt-link>

      <!-- Map -->
      <div v-if="displayMap" class="MapWrapper aspect-square w-full h-full">
        <GoogleMap
          ref="mapRef"
          api-key="runtimeConfig.public.googleMapsApiKey"
          style="width: 100%; height: 100%"
          :center="center"
          :zoom="14"
          :disable-default-ui="true"
        >
          <Marker
            :key="store.id"
            :options="{
              position: { lat: store.latitude, lng: store.longitude },
              icon: customIcon,
            }"
          />
        </GoogleMap>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Tooltip from '~/components/body/Tooltip.vue';
import { GoogleMap, Marker } from 'vue3-google-map';
import { StoreModel } from '~/constants/types/norce';
import { useGlobalContentStore } from '~/store/globalContent';
const runtimeConfig = useRuntimeConfig();
const globalContent = useGlobalContentStore();

// Details for exhibitions
const props = defineProps<{
  store: StoreModel;
  expanded: boolean;
  displayMap: boolean;
  infoWindow: boolean;
}>();

const emits = defineEmits<{
  (e: 'select', storeId: string): void;
  (e: 'close'): void;
}>();

// Compute array of floor types
const floors = computed(() => {
  return props.store.floors
    ? props.store.floors.split(',').map(f => f.trim())
    : [];
});

// Format distance display
function formatDistance(dist: number): string {
  return `${dist.toFixed(1)} km`;
}

// Handle selection (open and zoom)
function handleSelect() {
  if (!props.expanded) {
    emits('select', props.store.id);
  }
}

// Handle closing details
function toggleClose() {
  if (!props.expanded) {
    emits('select', props.store.id);
  } else {
    emits('close');
  }
}

const center = computed<google.maps.LatLngLiteral>(() => ({
  lat: props.store.latitude,
  lng: props.store.longitude,
}));

const customIcon = computed<google.maps.Icon | undefined>(() => {
  // bail out during SSR or before the maps API has loaded
  if (typeof window === 'undefined' || !window.google) return;
  return {
    url: '/logos/kahrs-logo-round.svg',
    scaledSize: new google.maps.Size(32, 32),
    anchor:     new google.maps.Point(16, 32),
  };
});

</script>
